import type { FieldType, LibraryField } from "./profileFieldsLibrary-hooks";
import {
  getProfileFieldsApi,
  saveProfileFieldsApi,
  deleteProfileFieldApi,
} from "../../../hooks/profileField.controller";

/**
 * SYSTEM PROFILE FIELDS — the org-wide, non-editable field definitions that are
 * identical for every customer/tenant.
 *
 * This is the single source of truth for *system* field definitions. It is
 * intentionally CODE-owned rather than per-tenant DB rows because:
 *   - they are the same for every tenant and not customer-editable,
 *   - they map to fixed company columns (name, ABN, contact, phone, …),
 *   - changing one is a reviewed deploy that applies everywhere at once.
 *
 * CUSTOM fields, by contrast, are per-tenant and come from the database via the
 * `/profilefield` API (see profileField.controller.js). The UI consumes the
 * MERGED list produced by `getProfileFields()` below.
 *
 * If the backend ever takes ownership of the system definitions too, keep ONE
 * copy — move `SYSTEM_PROFILE_FIELDS` server-side and have it serve the merged
 * list, at which point `getProfileFields()` just maps the response.
 */

/** A pure field DEFINITION. `id` is present only for custom (DB-backed) fields. */
export type ProfileFieldDef = {
  id?: string;
  key: string;
  label: string;
  type: FieldType;
  system: boolean;
  /** Built-in hardcoded company field — a stricter tier of `system` (see
   * CORE_PROFILE_FIELDS). Always `system: true` too, so it inherits the
   * read-only / never-saved handling. */
  builtIn?: boolean;
  options?: string[];
  /** Suggested default for "required in portal" when a group first adds this field. */
  defaultRequired?: boolean;
  /** Globally archived (system fields are effectively never archived). */
  deprecated?: boolean;
};

/** Runtime usage — derived server-side from group bindings + company values, NOT part of a definition. */
export type ProfileFieldUsage = {
  /** field key -> number of groups composing it */
  usedIn?: Record<string, number>;
  /** field key -> (option -> number of companies with it selected) */
  optionUsage?: Record<string, Record<string, number>>;
};

/**
 * BUILT-IN company fields — the five hard-coded, fixed inputs that live as their
 * own columns on the company form (Company Name, Company ID, Primary Contact
 * name + email, Phone). They are NOT composable library fields: they always
 * exist and cannot be added, removed, edited or duplicated.
 *
 * They are surfaced in the library (read-only, "BUILT-IN" badge) purely so admins
 * can see they already exist and don't create duplicate custom fields. Flagged
 * `system: true` as well, so they inherit every read-only / never-saved guard the
 * system fields already have (save diff + baseline both filter on `!system`).
 *
 * Keys match `CORE_FIELD_IDS` in the legacy company form
 * (html/js/pages/company-profile-values/index.js) so nothing renders twice.
 */
export const CORE_PROFILE_FIELDS: ProfileFieldDef[] = [
  {
    key: "company_name",
    label: "Company Name",
    type: "text",
    system: true,
    builtIn: true,
  },
  {
    key: "contact_name",
    label: "Primary Contact Name",
    type: "text",
    system: true,
    builtIn: true,
  },
  {
    key: "company_id",
    label: "Company ID",
    type: "text",
    system: true,
    builtIn: true,
  },
  {
    key: "contact_email",
    label: "Primary Contact Email",
    type: "email",
    system: true,
    builtIn: true,
  },
  {
    key: "phone",
    label: "Phone Number",
    type: "phone",
    system: true,
    builtIn: true,
  },
];

export const SYSTEM_PROFILE_FIELDS: ProfileFieldDef[] = [
  // NOTE: the five hard-coded company fields (Company Name, Company ID, Primary
  // Contact Name/Email, Phone) live in CORE_PROFILE_FIELDS above, not here —
  // they are fixed inputs on the company form, not composable library fields.
  { key: "trading_name", label: "Trading Name(s)", type: "text", system: true },
  {
    key: "abn",
    label: "ABN",
    type: "number",
    system: true,
    defaultRequired: true,
  },
  {
    key: "company_structure",
    label: "Company Structure",
    type: "dropdown_single",
    system: true,
    options: ["Sole Trader", "Partnership", "Company", "Trust", "Other"],
    defaultRequired: true,
  },
  {
    key: "street_address",
    label: "Street Address",
    type: "text",
    system: true,
  },
  { key: "suburb", label: "Suburb", type: "text", system: true },
  { key: "postcode", label: "Postcode", type: "text", system: true },
  {
    key: "registered_state",
    label: "State",
    type: "dropdown_single",
    system: true,
    options: ["VIC", "NSW", "QLD", "WA", "SA", "TAS", "NT", "ACT"],
  },
];

/** Expand a definition into the full record the UI renders, attaching runtime/usage data. */
function toLibraryField(
  def: ProfileFieldDef,
  usage?: ProfileFieldUsage
): LibraryField {
  return {
    // Custom fields carry their DB id (used for PUT/DELETE); system fields get a synthetic id.
    id: def.id || "lf_" + def.key,
    key: def.key,
    label: def.label,
    type: def.type,
    system: def.system,
    builtIn: def.builtIn || false,
    options: def.options || [],
    deprecated: def.deprecated || false,
    defaultRequired: def.defaultRequired || false,
    usedIn: usage?.usedIn?.[def.key] ?? 0,
    optionUsage: usage?.optionUsage?.[def.key],
  };
}

/**
 * Merge the code-owned fields with a tenant's custom fields into the shape the UI
 * consumes:  [ ...built-in (code), ...system (code), ...custom (DB) ].
 * Built-in and system fields always come first and are read-only; built-ins are
 * additionally flagged `builtIn: true` and can never be composed or duplicated.
 */
export function mergeProfileFields(
  customFields: ProfileFieldDef[] = [],
  usage?: ProfileFieldUsage
): LibraryField[] {
  return [
    ...CORE_PROFILE_FIELDS,
    ...SYSTEM_PROFILE_FIELDS,
    ...customFields,
  ].map((def) => toLibraryField(def, usage));
}

/** The stored `value` is a stringified array of the entered value(s); parse it back to options. */
function parseFieldValue(value: any): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Map a backend `/profilefield` record to a definition. The backend uses
 * `profile_field_id` (the key), `format` (the type) and `value` (a stringified
 * array of the entered value(s)); we normalise those to the UI's shape.
 */
function fromApiRecord(rec: any): ProfileFieldDef {
  const id = rec.profile_field_id || rec.profileFieldId || rec.id;
  return {
    id,
    key: rec.key || rec.field_key || id,
    label: rec.label || rec.name || "",
    type: (rec.format || rec.type || rec.field_type || "text") as FieldType,
    system: !!rec.system,
    options:
      rec.value !== undefined ? parseFieldValue(rec.value) : rec.options || [],
    deprecated: !!rec.deprecated,
    defaultRequired: !!(rec.defaultRequired || rec.default_required),
  };
}

/**
 * Body entry for POST /profilefield. The backend upserts by `profile_field_id`
 * (a client-generated uuid4), so the same shape is used for both new and edited
 * fields. `format` is the type and `value` is the entered value(s), stringified.
 * `system` is omitted — a field saved here is always a custom (non-system) field.
 */
function buildFieldPayload(def: {
  id: string;
  label: string;
  type: FieldType;
  options?: string[];
}) {
  return {
    profile_field_id: def.id,
    profile_type: "company",
    label: def.label,
    format: def.type,
    value: JSON.stringify(def.options || []),
  };
}

/**
 * Fetch the tenant's custom fields (`GET /profilefield`) and return the merged
 * library (system + custom). This is the contract the page consumes.
 */
export async function getProfileFields(): Promise<LibraryField[]> {
  const res = await getProfileFieldsApi();
  const records: any[] = (res && res.returnData) || [];
  const usage: ProfileFieldUsage = { usedIn: {}, optionUsage: {} };
  const custom = records
    .filter((r) => !r.system) // system fields come from code, never the API
    .map((r) => {
      const def = fromApiRecord(r);
      if (typeof r.usedIn === "number") usage.usedIn![def.key] = r.usedIn;
      if (r.optionUsage) usage.optionUsage![def.key] = r.optionUsage;
      return def;
    });
  return mergeProfileFields(custom, usage);
}

/**
 * Create/update custom fields in one POST. Both new and edited fields go here —
 * the backend upserts by `profile_field_id`. Pass every field that needs saving;
 * the request body is an array. Callers refresh via getProfileFields afterwards.
 */
export async function saveCustomFields(
  fields: { id: string; label: string; type: FieldType; options?: string[] }[]
) {
  return saveProfileFieldsApi(fields.map(buildFieldPayload));
}

/** Delete a single custom field (DELETE /profilefield/{profileFieldId}). */
export async function deleteCustomField(profileFieldId: string) {
  return deleteProfileFieldApi(profileFieldId);
}

/** Initial library shown before the API responds: system fields only. */
export const initialLibrary: LibraryField[] = mergeProfileFields([]);
